import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { usePrefs, formatPrice } from "@/context/PreferencesContext";
import { api, formatErr, fileUrl } from "@/lib/api";
import { toast } from "sonner";
import { Trash, ArrowRight, Truck } from "@phosphor-icons/react";

export default function Cart() {
  const { items, remove, updateQty, subtotal, clear } = useCart();
  const { user } = useAuth();
  const { currency } = usePrefs();
  const nav = useNavigate();
  const [loading, setLoading] = useState(false);
  const [estimate, setEstimate] = useState(null);
  const [estimating, setEstimating] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  // Fetch shipping estimate whenever cart or auth changes
  useEffect(() => {
    if (!user?.id || items.length === 0) {
      setEstimate(null);
      return;
    }
    let cancelled = false;
    (async () => {
      setEstimating(true);
      try {
        const payload = {
          items: items.map((i) => ({
            product_id: i.product_id, quantity: i.quantity,
            size: i.size || null, color: i.color || null,
          })),
          origin_url: window.location.origin,
        };
        const { data } = await api.post("/checkout/estimate", payload);
        if (!cancelled) setEstimate(data);
      } catch {
        if (!cancelled) setEstimate(null);
      } finally {
        if (!cancelled) setEstimating(false);
      }
    })();
    return () => { cancelled = true; };
  }, [items, user]);

  const checkout = async () => {
    if (!user || !user.id) return nav("/login", { state: { from: "/panier" } });
    if (!acceptedTerms) {
      toast.error("Vous devez accepter les politiques de MerchHub pour continuer");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        items: items.map((i) => ({
          product_id: i.product_id, quantity: i.quantity,
          size: i.size || null, color: i.color || null,
        })),
        origin_url: window.location.origin,
        currency: currency,
      };
      const { data } = await api.post("/checkout", payload);
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatErr(err));
      setLoading(false);
    }
  };

  const shipping = estimate?.shipping ?? null;
  const total = shipping != null ? subtotal + shipping : subtotal;

  return (
    <div className="max-w-[1600px] mx-auto px-6 lg:px-10 py-10" data-testid="cart-page">
      <h1 className="font-display text-6xl md:text-8xl uppercase border-b border-white/10 pb-6 mb-8">Panier</h1>
      {items.length === 0 ? (
        <div className="border border-white/10 p-16 text-center">
          <div className="font-display text-4xl uppercase">Panier vide</div>
          <Link to="/catalogue" className="mt-6 inline-block px-8 py-4 bg-[#FF3B00] text-white uppercase tracking-widest">Explorer</Link>
        </div>
      ) : (
        <div className="grid lg:grid-cols-[1fr_400px] gap-8">
          <div className="space-y-2">
            {items.map((it, i) => (
              <div key={i} className="border border-white/10 p-4 flex gap-4 items-center" data-testid={`cart-item-${i}`}>
                <div className="w-24 h-24 bg-[#111] flex-shrink-0">
                  {it.image ? <img src={fileUrl(it.image)} className="w-full h-full object-cover" alt="" /> : null}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs uppercase tracking-widest text-white/40">{it.artist_name}</div>
                  <div className="font-display text-xl uppercase truncate">{it.name}</div>
                  <div className="text-xs text-white/50 mt-1">
                    {it.size && <span className="mr-2">Taille: {it.size}</span>}
                    {it.color && <span>Couleur: {it.color}</span>}
                  </div>
                </div>
                <div className="flex items-center border border-white/20">
                  <button onClick={() => updateQty(i, it.quantity - 1)} className="w-10 h-10 hover:bg-white/5">−</button>
                  <div className="w-10 text-center">{it.quantity}</div>
                  <button onClick={() => updateQty(i, it.quantity + 1)} className="w-10 h-10 hover:bg-white/5">+</button>
                </div>
                <div className="font-display text-2xl w-24 text-right">{formatPrice(it.price * it.quantity, currency).display}</div>
                <button onClick={() => remove(i)} className="text-white/40 hover:text-[#FF3B00] p-2" data-testid={`cart-remove-${i}`}>
                  <Trash size={20} />
                </button>
              </div>
            ))}
          </div>

          <aside className="border border-white/10 p-6 h-fit sticky top-24" data-testid="cart-summary">
            <div className="text-xs uppercase tracking-[0.3em] text-[#FF3B00]">Récapitulatif</div>
            <div className="mt-4 space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-white/60">Sous-total</span><span>{formatPrice(subtotal, currency).display}</span></div>

              {!user?.id ? (
                <div className="flex justify-between text-white/60">
                  <span>Livraison</span>
                  <span className="text-xs">Connectez-vous pour calculer</span>
                </div>
              ) : estimating ? (
                <div className="flex justify-between text-white/60">
                  <span>Livraison</span><span className="text-xs">Calcul...</span>
                </div>
              ) : estimate?.shipping_breakdown?.length > 0 ? (
                <>
                  <div className="mt-3 pt-3 border-t border-white/10">
                    <div className="text-xs uppercase tracking-widest text-white/40 mb-2 flex items-center gap-1">
                      <Truck size={12} /> Livraison par pilote
                    </div>
                    {estimate.shipping_breakdown.map((sb) => (
                      <div key={sb.artist_id} className="flex justify-between text-xs mb-1">
                        <span className="text-white/60 truncate">{sb.artist_name}</span>
                        <span className={sb.shipping === 0 ? "text-green-400" : ""}>
                          {sb.shipping === 0 ? "GRATUITE" : formatPrice(sb.shipping, currency).display}
                        </span>
                      </div>
                    ))}
                    <div className="flex justify-between mt-2 pt-2 border-t border-white/10 text-sm">
                      <span className="text-white/60">Total livraison</span>
                      <span>{formatPrice(shipping || 0, currency).display}</span>
                    </div>
                  </div>
                </>
              ) : (
                <div className="flex justify-between">
                  <span className="text-white/60">Livraison</span>
                  <span className="text-green-400">GRATUITE</span>
                </div>
              )}

              <div className="flex justify-between border-t border-white/10 pt-3 mt-3 font-display text-2xl">
                <span>Total</span>
                <span>{formatPrice(total, currency).display} <span className="text-xs text-white/40 tracking-widest">{formatPrice(0, currency).suffix}</span></span>
              </div>
            </div>
            <label className="mt-6 flex gap-3 items-start cursor-pointer group" data-testid="accept-terms-label">
              <input
                type="checkbox"
                checked={acceptedTerms}
                onChange={(e) => setAcceptedTerms(e.target.checked)}
                data-testid="accept-terms-checkbox"
                className="mt-1 w-4 h-4 accent-[#FF3B00] cursor-pointer flex-shrink-0"
              />
              <span className="text-xs text-white/70 leading-relaxed group-hover:text-white">
                J'ai lu et j'accepte les{" "}
                <Link to="/faq" target="_blank" className="text-[#FF3B00] underline">politiques de remboursement, d'échange et de livraison</Link>{" "}
                de MerchHub. Je reconnais que toute transaction est finale du côté de MerchHub et que le pilote vendeur est seul responsable de l'expédition et des retours.
              </span>
            </label>
            <button onClick={checkout} disabled={loading || !acceptedTerms} data-testid="checkout-btn"
              className="w-full mt-4 py-4 bg-[#FF3B00] text-white uppercase tracking-widest hover:bg-[#E03500] inline-flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed">
              {loading ? "..." : <>Payer <ArrowRight size={16} /></>}
            </button>
            <button onClick={clear} className="w-full mt-2 py-2 text-xs uppercase tracking-widest text-white/40 hover:text-white">Vider le panier</button>
          </aside>
        </div>
      )}
    </div>
  );
}
